import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../lib/supabase";

type Brand = {
  id: number;
  name: string;
  logo_url: string;
  created_at?: string;
  updated_at?: string;
};

const BRAND_BUCKET = "brands-logos";
const PRODUCT_BUCKET = "product-images"; // fallback for older saved logos

const AdminBrandPage = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [newBrandName, setNewBrandName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const checkAdmin = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setMessage("Not logged in.");
        return;
      }

      const { data: admins, error } = await supabase
        .from("Admin")
        .select("*")
        .eq("user_id", user.id);

      if (error || !admins || admins.length === 0) {
        setMessage("Access denied: you are not an admin.");
      }
    };

    checkAdmin();
  }, []);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    const fetchBrands = async () => {
      const { data, error } = await supabase.from("Brand").select("*");
      if (error) {
        console.error("Error fetching brands:", error.message);
      } else {
        setBrands((data || []) as Brand[]);
      }
    };

    fetchBrands();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    if (!newBrandName.trim() || !selectedFile) {
      setMessage("Please provide a brand name and image.");
      return;
    }

    setLoading(true);

    try {
      const fileExt = selectedFile.name.split(".").pop();
      const fileName = `${uuidv4()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(BRAND_BUCKET)
        .upload(fileName, selectedFile, { upsert: false });

      if (uploadError) {
        console.error("Upload error:", uploadError.message);
        setMessage("Failed to upload image.");
        return;
      }

      const { data: urlData } = supabase.storage
        .from(BRAND_BUCKET)
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl || "";
      if (!publicUrl) {
        setMessage("Failed to generate public URL.");
        return;
      }

      const { data, error: insertError } = await supabase
        .from("Brand")
        .insert([{ name: newBrandName.trim(), logo_url: publicUrl }])
        .select()
        .single();

      if (insertError) {
        console.error("Insert error:", insertError.message);
        setMessage("Failed to add brand.");
        return;
      }

      setBrands((prev) => [...prev, data as Brand]);
      setMessage("Brand added successfully!");
      setNewBrandName("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setLoading(false);
    }
  };

  const getBucketAndPathFromUrl = (url: string) => {
    if (!url) return null;

    const marker = "/storage/v1/object/public/";
    const idx = url.indexOf(marker);
    if (idx === -1) return null;

    const rest = url.substring(idx + marker.length);
    const firstSlash = rest.indexOf("/");
    if (firstSlash === -1) return null;

    const bucket = rest.substring(0, firstSlash);
    const pathWithQuery = rest.substring(firstSlash + 1);
    const path = pathWithQuery.split("?")[0];

    if (!bucket || !path) return null;
    return { bucket, path };
  };

  const isLogoUrlSharedByOtherBrands = async (brandId: number, logoUrl: string) => {
    if (!logoUrl) return false;

    const { data, error } = await supabase
      .from("Brand")
      .select("id")
      .eq("logo_url", logoUrl)
      .neq("id", brandId)
      .limit(1);

    if (error) {
      // if we can't confirm, be conservative and treat as shared=false (or true)
      // I'll be conservative in *not* blocking deletes just because the check failed.
      console.warn("Logo shared-check failed:", error.message);
      return false;
    }

    return (data || []).length > 0;
  };

  const handleDelete = async (brand: Brand) => {
    const confirmed = window.confirm(
      `Delete brand "${brand.name}"?\n\nThis will also remove its logo file from storage (if it is not shared).`
    );
    if (!confirmed) return;

    setLoading(true);
    setMessage("");

    try {
      const { data: products, error: fetchError } = await supabase
        .from("Product")
        .select("id")
        .eq("brandId", brand.id);

      if (fetchError) {
        console.error("Failed to fetch products:", fetchError);
        alert("Failed to check products linked to this brand.");
        return;
      }

      if (products && products.length > 0) {
        const confirmedProducts = window.confirm(
          `This brand is linked to ${products.length} product(s).\n\nDo you want to delete the brand and ALL its products?`
        );
        if (!confirmedProducts) return;

        const { error: deleteProductsError } = await supabase
          .from("Product")
          .delete()
          .eq("brandId", brand.id);

        if (deleteProductsError) {
          console.error("Failed to delete related products:", deleteProductsError);
          alert("Failed to delete products related to this brand.");
          return;
        }
      }

      const { error: deleteBrandError } = await supabase
        .from("Brand")
        .delete()
        .eq("id", brand.id);

      if (deleteBrandError) {
        console.error("Failed to delete brand:", deleteBrandError);
        alert("Failed to delete brand.");
        return;
      }

      const logoUrl = (brand.logo_url || "").trim();
      if (logoUrl) {
        // optional: don't delete if shared by another brand
        const isShared = await isLogoUrlSharedByOtherBrands(brand.id, logoUrl);

        if (!isShared) {
          const parsed = getBucketAndPathFromUrl(logoUrl);

          if (parsed) {
            const { error: removeErr } = await supabase.storage
              .from(parsed.bucket)
              .remove([parsed.path]);

            if (removeErr) {
              console.warn("Logo file remove warning:", removeErr.message);
            }
          } else {
            const legacyPath = logoUrl.split(`/${PRODUCT_BUCKET}/`)[1]?.split("?")[0];
            if (legacyPath) {
              const { error: removeLegacyErr } = await supabase.storage
                .from(PRODUCT_BUCKET)
                .remove([legacyPath]);

              if (removeLegacyErr) {
                console.warn("Legacy logo remove warning:", removeLegacyErr.message);
              }
            } else {
              console.warn("Could not parse logo path from URL:", logoUrl);
            }
          }
        }
      }

      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setMessage("✅ Brand deleted (and logo cleaned up).");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-[#5c2f2f] mb-6">Manage Brands</h1>

      <form onSubmit={handleAdd} className="mb-6 space-y-4">
        <input
          type="text"
          placeholder="Brand name"
          value={newBrandName}
          onChange={(e) => setNewBrandName(e.target.value)}
          className="w-full border border-[#d3bcbc] p-2 rounded text-[#5c2f2f]"
        />

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          className="w-full border border-[#d3bcbc] p-2 rounded text-[#5c2f2f]"
        />

        {selectedFile && (
          <img
            src={URL.createObjectURL(selectedFile)}
            alt="Preview"
            className="w-24 h-24 object-contain rounded"
          />
        )}

        <button
          type="submit"
          disabled={loading}
          className="bg-[#5c2f2f] hover:bg-[#7a4343] text-white px-4 py-2 rounded transition disabled:opacity-60"
        >
          {loading ? "Adding..." : "Add Brand"}
        </button>

        {message && (
          <p className="text-sm text-center text-red-600 mt-2">{message}</p>
        )}
      </form>

      <h2 className="text-xl font-semibold mb-2 text-[#5c2f2f]">
        Current Brands
      </h2>

      <ul className="space-y-2">
        {brands.map((brand) => (
          <li
            key={brand.id}
            className="flex items-center justify-between bg-[#fff0f0] p-2 rounded shadow-sm"
          >
            <div className="flex items-center gap-4">
              <img
                src={brand.logo_url}
                alt={brand.name}
                className="w-10 h-10 object-contain"
              />
              <span className="font-medium text-[#5c2f2f]">{brand.name}</span>
            </div>

            <button
              onClick={() => handleDelete(brand)}
              className="text-red-600 hover:underline text-sm"
              disabled={loading}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AdminBrandPage;
